"use client"
import type React from "react"
import { useEffect, useState, useRef } from "react"
import Button from "../../../components/common/Button"
import { FileText, Video, HelpCircle, Upload, Play, ChevronDown, ChevronRight, Edit2, MoreVertical, Move, Plus, Trash2, X } from "lucide-react"
import Modal from "../../../components/common/Modal"
import InputField from "../../../components/common/InputField"
import Dropdown from "../../../components/common/Dropdown"
import {
  DragDropContext,
  Droppable,
  Draggable,
} from '@hello-pangea/dnd'

import { 
  createCourseModule, 
  createCourseModuleLesson, 
  deleteCourseModule,
  softDeleteCourseModule,
  softDeleteCourseModuleLesson,
  getAllCourseModules,
  getCourseModuleById,
  getLessonsByModuleId,
  updateCourseModule,
  updateCourseModuleLesson,
  updateLessonOrder,
} from "../../../api/courseService"

import Hls from "hls.js";
import MDEditor from '@uiw/react-md-editor'
import '@uiw/react-md-editor/markdown-editor.css'
import '@uiw/react-markdown-preview/markdown.css'
import useAuth from "../../../hooks/useAuth"

interface CurriculumStepProps {
  data: any
  onChange: (data: any) => void
  onNext?: () => void
  isEditMode?: boolean
  onDirtyChange?: (dirty: boolean) => void
  onLessonFormDirtyChange?: (dirty: boolean) => void
}

interface Module {
  id: string
  title: string
  description: string
  lessons: Lesson[]
  isOpen: boolean
  order: number
  _id?: string
}

interface Lesson {
  id: string
  title: string
  description: string
  type: 'text' | 'video' | 'document' | 'quiz' | 'assignment' | 'audio'
  content?: string
  duration?: number | null
  order?: number;
  fileUrl?: string
  thumbnailUrl?: string
  questions?: Question[]
  videoStatus?: 'processing' | 'ready' | 'failed'
  _id?: string
}

interface Question {
  questionText: string
  options: Option[]
}

interface Option {
  answertext: string
  isCorrect: boolean
  explanation: string
}

const lessonTypes = [
  { id: 'video', label: 'Video', icon: <Video className="text-red-500" />, color: 'bg-red-50' },
  { id: 'document', label: 'Document', icon: <FileText className="text-green-500" />, color: 'bg-green-50' },
  { id: 'quiz', label: 'Quiz', icon: <HelpCircle className="text-purple-500" />, color: 'bg-purple-50' },
]

const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return ''
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  if (h > 0) return `${h} hr ${m} min`
  if (m > 0 && s > 0) return `${m} min ${s} sec`
  if (m > 0) return `${m} min`
  return `${s} sec`
}

const HLSVideoPreview: React.FC<{
  src: string;
  poster?: string;
}> = ({ src, poster }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !src) return;

    // Clean up previous HLS instance
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    // Protected /stream URLs need HLS.js in Chrome
    if (src.includes("/stream/") || src.includes(".m3u8")) {
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
        });

        hls.loadSource(src);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          console.log("HLS manifest parsed successfully");
        });

        hls.on(Hls.Events.ERROR, (_event, data) => {
          console.error("HLS playback error:", data);

          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                console.log("HLS network error - retrying...");
                hls.startLoad();
                break;

              case Hls.ErrorTypes.MEDIA_ERROR:
                console.log("HLS media error - recovering...");
                hls.recoverMediaError();
                break;

              default:
                console.error("Fatal HLS error");
                hls.destroy();
                break;
            }
          }
        });

        hlsRef.current = hls;
      } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
        // Safari native HLS support
        video.src = src;
      } else {
        console.error("HLS is not supported in this browser");
      }
    } else {
      // Normal MP4/blob video
      video.src = src;
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src]);

  return (
    <video
      ref={videoRef}
      controls
      playsInline
      preload="metadata"
      poster={poster}
      className="w-full h-full object-cover"
      onLoadedMetadata={(e) => {
        console.log("VIDEO LOADED:", {
          src: e.currentTarget.currentSrc,
          duration: e.currentTarget.duration,
        });
      }}
      onCanPlay={() => {
        console.log("VIDEO CAN PLAY");
      }}
      onError={(e) => {
        console.error("VIDEO ELEMENT ERROR:", e.currentTarget.error);
      }}
    />
  );
};

const CurriculumStep: React.FC<CurriculumStepProps> = ({ data, onChange, onNext, isEditMode = false, onDirtyChange, onLessonFormDirtyChange, }) => {
  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false)
  const [modules, setModules] = useState<Module[]>(data.modules || [])
  const [activeModuleId, setActiveModuleId] = useState<string | null>(null)
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null)
  const [editingModule, setEditingModule] = useState<Module | null>(null)
  const [courseId, setCourseId] = useState<string | null>(data._id || null)
  const [loading, setLoading] = useState(false)
  const [showLessonForm, setShowLessonForm] = useState(false)
  
  const [hasUnsavedLessonOrder, setHasUnsavedLessonOrder] = useState(false);
  const [savingLessonOrder, setSavingLessonOrder] = useState(false);

  const [hasUnsavedLessonChanges, setHasUnsavedLessonChanges] = useState(false);

  const [moduleForm, setModuleForm] = useState({
    title: '',
    description: ''
  })
  
  const [lessonForm, setLessonForm] = useState({
    title: '',
    description: '',
    type: 'video' as 'video' | 'document' | 'quiz' | 'text' | 'assignment' | 'audio',
    duration: null as number | null,
    contentFile: null as File | null,
    thumbnailFile: null as File | null,
    thumbnailUrl: '',
    questions: [] as Question[]
  })
  
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null)
  const [thumbnailAspectRatio, setThumbnailAspectRatio] = useState<number | null>(null)
  const [videoPreview, setVideoPreview] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [uploadProgress, setUploadProgress] = useState<number>(0)

  const hlsRef = useRef<Hls | null>(null);
  
  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 2000)
  }

  const markCurriculumDirty = () => {
    onDirtyChange?.(true)
  }

    const markLessonFormDirty = () => {
    setHasUnsavedLessonChanges(true)
    onLessonFormDirtyChange?.(true)
  }

  const clearLessonFormDirty = () => {
    setHasUnsavedLessonChanges(false)
    onLessonFormDirtyChange?.(false)
  }
  const [showLessonTypeChangeModal, setShowLessonTypeChangeModal] = useState(false)
  const [pendingLessonType, setPendingLessonType] = useState<string | null>(null)

  const hasInitializedModule = useRef(false);

useEffect(() => {
  setModules(data.modules || []);
  setCourseId(data._id || null);

  if (
    !hasInitializedModule.current &&
    data.modules &&
    data.modules.length > 0
  ) {
    setActiveModuleId(data.modules[0].id);
    hasInitializedModule.current = true;
  }
}, [data.modules, data._id]);

useEffect(() => {
  if (!activeModuleId && modules.length > 0) {
    setActiveModuleId(modules[0].id);
  }
}, [modules, activeModuleId]);

useEffect(() => {
  setModules(data.modules || []);
  setCourseId(data._id || null);

  if (
    !hasInitializedModule.current &&
    data.modules &&
    data.modules.length > 0
  ) {
    setActiveModuleId(data.modules[0].id);
    hasInitializedModule.current = true;
  }
}, [data.modules, data._id]);

 useEffect(() => {
  return () => {
    if (thumbnailPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(thumbnailPreview);
    }

    if (videoPreview?.startsWith("blob:")) {
      URL.revokeObjectURL(videoPreview);
    }
  };
}, []);
   
  
  useEffect(() => {
    if (lessonForm.type !== 'quiz') {
      setLessonForm(prev => ({ ...prev, questions: [] }))
    }
  }, [lessonForm.type])
  
  useEffect(() => {
    const fetchAndPopulateModules = async () => {
      const currentCourseId = data._id;
      
      if (isEditMode && currentCourseId) {
        try {
          setLoading(true);
          
          const response = await getAllCourseModules();
          
          if (response.status === 200 && response.data) {
            const filteredModules = response.data.filter(
              (module: any) => module.courseId && module.courseId._id === currentCourseId
            );
            
            filteredModules.sort((a: any, b: any) => a.order - b.order);
            
            const formattedModules = await Promise.all(filteredModules.map(async (module: any) => {
              const lessonsResponse = await getLessonsByModuleId(module._id);
              let lessons = [];
              
              if (lessonsResponse.status === 200 && lessonsResponse.data) {
               lessons = lessonsResponse.data
                  .map((lesson: any) => ({
                    id: lesson._id,
                    title: lesson.title,
                    description: lesson.description,
                    type: lesson.lessonType === 'assignment'
                      ? 'document'
                      : (lesson.lessonType || 'video'),
                    content: lesson.content,
                    duration: lesson.duration,
                    order: lesson.order,
                    fileUrl: lesson.content,
                    thumbnailUrl: lesson.lessonThumbnail,
                    questions: lesson.questions || [],
                    videoStatus: lesson.videoStatus,
                    _id: lesson._id
                  }))
                  .sort((a: Lesson, b: Lesson) => {
                    return (a.order ?? 0) - (b.order ?? 0);
                  });
              }
              
              return {
                id: module._id,
                title: module.title,
                description: module.description,
                lessons: lessons,
                isOpen: false,
                order: module.order,
                _id: module._id
              };
            }));
            
            setModules(formattedModules);
            updateModules(formattedModules);
            
            if (formattedModules.length > 0) {
              setActiveModuleId(formattedModules[0].id);
            }
          }
        } catch (error) {
          console.error("Error fetching course modules and lessons:", error);
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchAndPopulateModules();
  }, [isEditMode, data._id]);
  
  const updateModules = (updatedModules: Module[]) => {
    const updatedCourse = {
      ...data,
      modules: updatedModules
    }
    onChange(updatedCourse)
  }
  
  const onDragEnd = (result: any) => {
  if (!result.destination || !activeModuleId) return;

  const sourceIndex = result.source.index;
  const destinationIndex = result.destination.index;

  if (sourceIndex === destinationIndex) return;

  const updatedModules = modules.map(module => {
    if (module.id !== activeModuleId) {
      return module;
    }

    const reorderedLessons = [...module.lessons];

    const [movedLesson] = reorderedLessons.splice(sourceIndex, 1);

    reorderedLessons.splice(destinationIndex, 0, movedLesson);

    const lessonsWithUpdatedOrder = reorderedLessons.map(
      (lesson, index) => ({
        ...lesson,
        order: index + 1,
      })
    );

    return {
      ...module,
      lessons: lessonsWithUpdatedOrder,
    };
  });

  // Update local UI state
  setModules(updatedModules);

  // Update parent form state OUTSIDE setModules
  updateModules(updatedModules);

  // Enable Save Order button
  setHasUnsavedLessonOrder(true);

  markCurriculumDirty()
};

const handleSaveLessonOrder = async () => {
  if (!activeModuleId || !hasUnsavedLessonOrder) return;

  const activeModule = modules.find(
    module => module.id === activeModuleId
  );

  if (!activeModule) return;

  try {
    setSavingLessonOrder(true);

    const results = await Promise.all(
      activeModule.lessons.map((lesson, index) =>
        updateLessonOrder(lesson.id, index + 1)
      )
    );

    const failedResult = results.find(
      result => !result || result.status >= 400
    );

    if (failedResult) {
      throw new Error(
        failedResult.message || "Failed to save lesson order"
      );
    }

    setHasUnsavedLessonOrder(false);

    alert("Lesson order updated successfully");
  } catch (error: any) {
    console.error("Error saving lesson order:", error);

    alert(error.message || "Failed to save lesson order");
  } finally {
    setSavingLessonOrder(false);
  }
};
  
  const handleCreateModule = async () => {
    if (!moduleForm.title.trim() || !courseId) return
    
    try {
      setLoading(true)
      const response = await createCourseModule({
        title: moduleForm.title,
        description: moduleForm.description,
        order: modules.length + 1,
        courseId: courseId
      })
      
      if (response.status >= 400) {
        console.error("Error creating module:", response.message)
        return
      }
      const newModule = {
        id: response.data._id,
        title: response.data.title,
        description: response.data.description,
        lessons: [],
        isOpen: true,
        order: response.data.order
      }
      
      const updatedModules = [...modules, newModule]
      setModules(updatedModules)
      setActiveModuleId(newModule.id)
      updateModules(updatedModules)
      
      markCurriculumDirty()

      setModuleForm({ title: '', description: '' })
      setIsModuleModalOpen(false)
    } catch (error) {
      console.error("Error creating module:", error)
    } finally {
      setLoading(false)
    }
  }
  
  const handleEditModule = async (module: Module) => {
    try {
      setLoading(true);
      
      const response = await getCourseModuleById(module.id);
      
      if (response.status === 200 && response.data) {
        const moduleData = response.data;
        
        setEditingModule(module);
        setModuleForm({
          title: moduleData.title,
          description: moduleData.description || ''
        });
        setIsModuleModalOpen(true);
      } else {
        console.error("Error fetching module data:", response.message);
        setEditingModule(module);
        setModuleForm({
          title: module.title,
          description: module.description || ''
        });
        setIsModuleModalOpen(true);
      }
    } catch (error) {
      console.error("Error in handleEditModule:", error);
      setEditingModule(module);
      setModuleForm({
        title: module.title,
        description: module.description || ''
      });
      setIsModuleModalOpen(true);
    } finally {
      setLoading(false);
    }
  }
  
const handleDeleteModule = async (id: string) => {
  try {
    setLoading(true)

    const response = await softDeleteCourseModule(
      id,
      user?._id || ""
    )
    
    if (response.status >= 400) {
      console.error("Error deleting module:", response.message)
      return
    }

    const updatedModules = modules.filter(module => module.id !== id)

    setModules(updatedModules)

    if (activeModuleId === id) {
      setActiveModuleId(null)
    }

    updateModules(updatedModules)
    markCurriculumDirty()

  } catch (error) {
    console.error("Error deleting module:", error)
  } finally {
    setLoading(false)
  }
}

  const { user } = useAuth();

  const handleUpdateModule = async () => {
    if (!editingModule || !moduleForm.title.trim()) return
    
    try {
      setLoading(true);
      
      const response = await updateCourseModule({
        id: editingModule.id,
        title: moduleForm.title,
        description: moduleForm.description,
        order: editingModule.order,
        user_id: user?._id || "",
      });
      
      if (response.status === 200 && response.data) {
        const updatedModules = modules.map(m => 
          m.id === editingModule.id 
            ? { 
                ...m, 
                title: response.data.title, 
                description: response.data.description,
                _id: response.data._id
              }
            : m
        )
        
        setModules(updatedModules)
        updateModules(updatedModules)

        markCurriculumDirty()
        setModuleForm({ title: '', description: '' })
        setEditingModule(null)
        setIsModuleModalOpen(false)
      } else {
        console.error("Error updating module:", response.message);
      }
    } catch (error) {
      console.error("Error updating module:", error);
    } finally {
      setLoading(false);
    }
  }
  
const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];

  if (!file) return;

  // =========================
  // DOCUMENT
  // =========================
  if (lessonForm.type === "document") {
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!validTypes.includes(file.type)) {
      alert("Please upload a PDF, DOC, or DOCX file");
      return;
    }

    markLessonFormDirty();

    setLessonForm((prev) => ({
      ...prev,
      contentFile: file,
    }));

    return;
  }

  // =========================
  // VIDEO
  // =========================
  if (lessonForm.type === "video") {
    const validTypes = [
      "video/mp4",
      "video/quicktime",
      "video/x-msvideo",
      "video/webm",
      "application/x-mpegURL",
    ];

    if (!validTypes.includes(file.type)) {
      alert(
        "Please upload a valid video file (MP4, MOV, AVI, WEBM, M3U8)"
      );
      return;
    }

    markLessonFormDirty();

    // Revoke previous preview URL if there was one
    setVideoPreview((previousUrl) => {
      if (previousUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(previousUrl);
      }

      return URL.createObjectURL(file);
    });

    // Separate temporary URL only for reading metadata
    const metadataUrl = URL.createObjectURL(file);

    const video = document.createElement("video");
    video.preload = "metadata";

    video.onloadedmetadata = () => {
      const durationInSeconds = Math.ceil(video.duration);

      setLessonForm((prev) => ({
        ...prev,
        contentFile: file,
        duration: durationInSeconds,
      }));

      URL.revokeObjectURL(metadataUrl);
    };

    video.onerror = () => {
      URL.revokeObjectURL(metadataUrl);

      setLessonForm((prev) => ({
        ...prev,
        contentFile: file,
      }));
    };

    video.src = metadataUrl;

    // Make sure contentFile is set immediately
    setLessonForm((prev) => ({
      ...prev,
      contentFile: file,
    }));
  }
};
  
  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    markLessonFormDirty();
    const file = e.target.files?.[0]
    if (file) {
      if (!file.type.match('image.*')) {
        alert('Please select an image file')
        return
      }
      
      const previewUrl = URL.createObjectURL(file)
      setThumbnailPreview(previewUrl)
      
      const img = new Image()
      img.onload = function() {
        const aspectRatio = img.width / img.height
        setThumbnailAspectRatio(aspectRatio)
        
        const targetRatio = 16/9
        const tolerance = 0.05
        if (Math.abs(aspectRatio - targetRatio) > tolerance) {
          alert(`Warning: The image aspect ratio is ${aspectRatio.toFixed(2)} (recommended: 16:9 ≈ 1.78)`)
        }
      }
      img.src = previewUrl
      
      setLessonForm({...lessonForm, thumbnailFile: file})
    }
  }
  
  const handleRemoveVideo = () => {
    markLessonFormDirty();
    // Clear both the new file and the existing file URL when editing
    setLessonForm({...lessonForm, contentFile: null, duration: null})
    
    // If we're editing a lesson, also clear the existing fileUrl
    if (editingLesson) {
      setEditingLesson({
        ...editingLesson,
        fileUrl: undefined
      });
    }
    
    if (videoPreview) {
      // Only revoke blob URLs, not server URLs
      if (videoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(videoPreview)
      }
      setVideoPreview(null)
    }
  }
  
  const handleRemoveThumbnail = () => {
    markLessonFormDirty();
    // Clear both the new file and the existing file URL when editing
    setLessonForm({...lessonForm, thumbnailFile: null, thumbnailUrl: ''})
    
    // If we're editing a lesson, also clear the existing thumbnailUrl
    if (editingLesson) {
      setEditingLesson({
        ...editingLesson,
        thumbnailUrl: undefined
      });
    }
    
    if (thumbnailPreview) {
      // Only revoke blob URLs, not server URLs
      if (thumbnailPreview.startsWith('blob:')) {
        URL.revokeObjectURL(thumbnailPreview)
      }
      setThumbnailPreview(null)
    }
    setThumbnailAspectRatio(null)
  }
  
  const addQuestion = () => {
    markLessonFormDirty();
    const newQuestion: Question = {
      questionText: '',
      options: [
        { answertext: '', isCorrect: false, explanation: '' },
        { answertext: '', isCorrect: false, explanation: '' }
      ]
    }
    setLessonForm(prev => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }))
  }
  
  const removeQuestion = (index: number) => {
    markLessonFormDirty();
    setLessonForm(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }))
  }
  
  const updateQuestionText = (index: number, text: string) => {
    markLessonFormDirty();
    setLessonForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === index ? { ...q, questionText: text } : q
      )
    }))
  }
  
  const addOption = (questionIndex: number) => {
    markLessonFormDirty();
    if (lessonForm.questions[questionIndex].options.length >= 6) return
    
    setLessonForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { 
              ...q, 
              options: [...q.options, { answertext: '', isCorrect: false, explanation: '' }] 
            } 
          : q
      )
    }))
  }
  
  const removeOption = (questionIndex: number, optionIndex: number) => {
    markLessonFormDirty();
    if (lessonForm.questions[questionIndex].options.length <= 2) return
    
    setLessonForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { ...q, options: q.options.filter((_, j) => j !== optionIndex) } 
          : q
      )
    }))
  }
  
  const updateOptionText = (questionIndex: number, optionIndex: number, text: string) => {
    markLessonFormDirty();
    setLessonForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { 
              ...q, 
              options: q.options.map((o, j) => 
                j === optionIndex ? { ...o, answertext: text } : o
              ) 
            } 
          : q
      )
    }))
  }
  
  const toggleCorrect = (questionIndex: number, optionIndex: number) => {
    markLessonFormDirty();
    setLessonForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { 
              ...q, 
              options: q.options.map((o, j) => 
                j === optionIndex 
                  ? { ...o, isCorrect: !o.isCorrect } 
                  : { ...o, isCorrect: false }
              ) 
            } 
          : q
      )
    }))
  }
  
  const updateExplanation = (questionIndex: number, optionIndex: number, text: string) => {
    markLessonFormDirty();
    setLessonForm(prev => ({
      ...prev,
      questions: prev.questions.map((q, i) => 
        i === questionIndex 
          ? { 
              ...q, 
              options: q.options.map((o, j) => 
                j === optionIndex ? { ...o, explanation: text } : o
              ) 
            } 
          : q
      )
    }))
  }
  
  const handleAddLesson = async () => {
    if (!lessonForm.title.trim() || !activeModuleId || !courseId) return
    
    if (lessonForm.type === 'quiz') {
      if (lessonForm.questions.length === 0) {
        alert("Please add at least one question for the quiz lesson.")
        return
      }
      
      for (let i = 0; i < lessonForm.questions.length; i++) {
        const question = lessonForm.questions[i]
        
        if (!question.questionText.trim()) {
          alert(`Please enter text for question ${i + 1}.`)
          return
        }
        
        const emptyOptionIndex = question.options.findIndex(option => !option.answertext.trim())
        if (emptyOptionIndex !== -1) {
          alert(`Please enter text for all options in question ${i + 1}.`)
          return
        }
        
        const hasCorrectAnswer = question.options.some(option => option.isCorrect)
        if (!hasCorrectAnswer) {
          alert(`Please select the correct answer for question ${i + 1}.`)
          return
        }
      }
    }
    
    try {
      setLoading(true)
      
      const questionsForApi = lessonForm.questions.map(q => ({
        questionText: q.questionText,
        options: q.options.map(o => ({
          answertext: o.answertext,
          isCorrect: o.isCorrect,
          explanation: o.explanation
        }))
      }))
      
      const lessonTypeForApi = lessonForm.type === 'document' ? 'assignment' : lessonForm.type
      
      const payload: any = {
        coursemoduleId: activeModuleId,
        courseId: courseId,
        title: lessonForm.title,
        description: lessonForm.description,
        lessonType: lessonTypeForApi,
        order: (activeModule?.lessons?.length || 0) + 1,
        ...(lessonForm.type !== 'document' && { duration: lessonForm.duration || null })
      };
      
      if (lessonForm.contentFile) {
        payload.file = lessonForm.contentFile;
      }
      
      if (lessonForm.thumbnailFile) {
        payload.thumbnailFile = lessonForm.thumbnailFile;
      }
      
      if (questionsForApi.length > 0) {
        payload.questions = questionsForApi;
      }
      
      setUploadProgress(0)
      const response = await createCourseModuleLesson(
        payload,
        lessonForm.type === 'video' && lessonForm.contentFile
          ? (pct) => setUploadProgress(pct)
          : undefined
      )

      if (response.status >= 400) {
        showToast(response.message || "Failed to create lesson. Please try again.", 'error')
        return
      }

      if (response?.data) {
        const newLesson: Lesson = {
          id: response.data._id,
          title: response.data.title,
          description: response.data.description || lessonForm.description,
          type: lessonForm.type,
          duration: response.data.duration || lessonForm.duration,
          fileUrl: response.data.fileUrl || (lessonForm.contentFile ? URL.createObjectURL(lessonForm.contentFile) : undefined),
          thumbnailUrl: response.data.thumbnailUrl || lessonForm.thumbnailUrl,
          questions: response.data.questions,
          videoStatus: response.data.videoStatus,
        }

        const updatedModules = modules.map(module => {
          if (module.id === activeModuleId) {
            return {
              ...module,
              lessons: [...module.lessons, newLesson]
            }
          }
          return module
        })

        setModules(updatedModules)
        resetLessonForm()
        setShowLessonForm(false)
        updateModules(updatedModules)
        clearLessonFormDirty()
        markCurriculumDirty()

        showToast("Lesson created successfully!", 'success')
      }
    } catch (error) {
      showToast("Something went wrong. Please try again.", 'error')
      console.error("Error adding lesson:", error)
    } finally {
      setLoading(false)
      setUploadProgress(0)
    }
  }
  
  const handleUpdateLesson = async () => {
    if (!editingLesson || !lessonForm.title.trim() || !activeModuleId) return
    
    try {
      setLoading(true);
      
      const questionsForApi = lessonForm.questions.map(q => ({
        questionText: q.questionText,
        options: q.options.map(o => ({
          answertext: o.answertext,
          isCorrect: o.isCorrect,
          explanation: o.explanation
        }))
      }));
      
      const lessonTypeForApi = lessonForm.type === 'document' ? 'assignment' : lessonForm.type;
      
      const payload: any = {
        id: editingLesson.id,
        title: lessonForm.title,
        description: lessonForm.description,
        lessonType: lessonTypeForApi,
        ...(lessonForm.type !== 'document' && { duration: lessonForm.duration || null })
      };
      
      // Handle content file
      if (!lessonForm.contentFile && !videoPreview && editingLesson.fileUrl === undefined) {
        console.log("Video file removed - will keep existing behavior");
      } else if (lessonForm.contentFile) {
        payload.file = lessonForm.contentFile;
      }
      
      // Handle thumbnail for video and non-video lessons
      if (lessonForm.type === 'video') {
        if (lessonForm.thumbnailFile) {
          payload.lessonThumbnail = lessonForm.thumbnailFile;
        } else if (thumbnailPreview === null && editingLesson.thumbnailUrl === undefined) {
          // User removed the existing thumbnail
          payload.lessonThumbnail = null;
        }
      } else {
        if (lessonForm.thumbnailFile) {
          payload.thumbnailFile = lessonForm.thumbnailFile;
        }
      }
      
      if (questionsForApi.length > 0) {
        payload.questions = questionsForApi;
      }
      
      setUploadProgress(0)

      const response = await updateCourseModuleLesson(
        {
          ...payload,
          user_id: JSON.parse(localStorage.getItem("userData") || "{}")._id,
        },
        lessonForm.type === 'video' && lessonForm.contentFile
          ? (pct) => setUploadProgress(pct)
          : undefined
      );

      if (response.status >= 400) {
        console.error("Error updating lesson:", response.message);
        return;
      }
      
      if (response?.data) {
        const updatedLesson: Lesson = {
          id: response.data._id,
          title: response.data.title,
          description: response.data.description || lessonForm.description,
          type: lessonForm.type,
          duration: response.data.duration || lessonForm.duration,
          order: response.data.order ?? editingLesson.order,
          fileUrl: response.data.fileUrl || (lessonForm.contentFile ? URL.createObjectURL(lessonForm.contentFile) : undefined),
          thumbnailUrl: response.data.thumbnailUrl || lessonForm.thumbnailUrl,
          questions: response.data.questions
        };
        
        const updatedModules = modules.map(module => {
          if (module.id === activeModuleId) {
            return {
              ...module,
              lessons: module.lessons.map(lesson => 
                lesson.id === editingLesson.id ? updatedLesson : lesson
              )
            };
          }
          return module;
        });
        
        setModules(updatedModules);
        resetLessonForm();
        setEditingLesson(null);
        setShowLessonForm(false);
        updateModules(updatedModules);
        clearLessonFormDirty()
        markCurriculumDirty();
        
      }
    } catch (error) {
      console.error("Error updating lesson:", error);
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };
  
  const resetLessonForm = () => {
    setLessonForm({ 
      title: '', 
      description: '',
      type: 'video', 
      duration: null,
      contentFile: null,
      thumbnailFile: null,
      thumbnailUrl: '',
      questions: []
    })
    setThumbnailPreview(null)
    setThumbnailAspectRatio(null)
    setVideoPreview(null)
  }
  
  const handleDeleteLesson = async (lessonId: string) => {
  if (!activeModuleId) return

  try {
    setLoading(true)

    const response = await softDeleteCourseModuleLesson(
      lessonId,
      user?._id || ""
    )

    if (response.status >= 400) {
      console.error("Error deleting lesson:", response.message)
      return
    }

    const updatedModules = modules.map(module => {
      if (module.id === activeModuleId) {
        return {
          ...module,
          lessons: module.lessons.filter(
            lesson => lesson.id !== lessonId
          )
        }
      }

      return module
    })

    setModules(updatedModules)
    updateModules(updatedModules)
    markCurriculumDirty()

  } catch (error) {
    console.error("Error deleting lesson:", error)
  } finally {
    setLoading(false)
  }
}
  
  const handleEditLesson = async (lesson: Lesson) => {
    try {
      setLoading(true);
      
      const response = await getLessonsByModuleId(activeModuleId!);
      
      if (response.status === 200 && response.data) {
        const lessonData = response.data.find((l: any) => l._id === lesson.id);
        
        if (lessonData) {
          const updatedLesson: Lesson = {
            id: lessonData._id,
            title: lessonData.title,
            description: lessonData.description || '',
            type: lessonData.lessonType === 'assignment' ? 'document' : (lessonData.lessonType || 'video'),
            duration: lessonData.duration !== undefined ? lessonData.duration : 0,
            //order: response.data.order ?? editingLesson.order,
            order: lessonData.order ?? lesson.order,
            fileUrl: lessonData.content,
            thumbnailUrl: lessonData.lessonThumbnail,
            questions: lessonData.questions || [],
            _id: lessonData._id
          };
          
          const updatedModules = modules.map(module => {
            if (module.id === activeModuleId) {
              const updatedLessons = module.lessons.map(l => 
                l.id === lesson.id ? updatedLesson : l
              );
              return { ...module, lessons: updatedLessons };
            }
            return module;
          });
          
          setModules(updatedModules);
          updateModules(updatedModules);
          
          setEditingLesson(updatedLesson);
          
          const questionsForForm = updatedLesson.questions ? updatedLesson.questions.map(q => ({
            questionText: q.questionText,
            options: q.options.map(o => ({
              answertext: o.answertext,
              isCorrect: o.isCorrect,
              explanation: o.explanation
            }))
          })) : [];
          
          const lessonTypeForUI = updatedLesson.type === 'assignment' ? 'document' : updatedLesson.type;
          
          setLessonForm({
            title: updatedLesson.title,
            description: updatedLesson.description || '',
            type: lessonTypeForUI,
            duration: updatedLesson.duration !== undefined ? updatedLesson.duration : null,
            contentFile: null,
            thumbnailFile: null,
            thumbnailUrl: updatedLesson.thumbnailUrl || '',
            questions: questionsForForm
          });

          setHasUnsavedLessonChanges(false);
          
          if (updatedLesson.thumbnailUrl) {
            setThumbnailPreview(updatedLesson.thumbnailUrl);
          }
          
          if (updatedLesson.fileUrl && updatedLesson.type === 'video') {
            setVideoPreview(updatedLesson.fileUrl);
          }
          
          setShowLessonForm(true);
        }
      }
    } catch (error) {
      console.error("Error fetching lesson data:", error);
      setEditingLesson(lesson);
      
      const questionsForForm = lesson.questions ? lesson.questions.map(q => ({
        questionText: q.questionText,
        options: q.options.map(o => ({
          answertext: o.answertext,
          isCorrect: o.isCorrect,
          explanation: o.explanation
        }))
      })) : [];
      
      const lessonTypeForUI = lesson.type === 'assignment' ? 'document' : lesson.type;
      
      setLessonForm({
        title: lesson.title,
        description: lesson.description || '',
        type: lessonTypeForUI,
        duration: lesson.duration !== undefined ? lesson.duration : null,
        contentFile: null,
        thumbnailFile: null,
        thumbnailUrl: lesson.thumbnailUrl || '',
        questions: questionsForForm
      });

      setHasUnsavedLessonChanges(false);
      
      if (lesson.thumbnailUrl) {
        setThumbnailPreview(lesson.thumbnailUrl);
      }
      
      if (lesson.fileUrl && lesson.type === 'video') {
        setVideoPreview(lesson.fileUrl);
      }
      
      setShowLessonForm(true);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancelLessonForm = () => {
    resetLessonForm()
    setEditingLesson(null)
    setShowLessonForm(false)
    clearLessonFormDirty()
  }
  
  const handleLessonTypeChange = (type: string) => {
    if (lessonForm.title.trim() !== '' || lessonForm.description.trim() !== '') {
      setPendingLessonType(type)
      setShowLessonTypeChangeModal(true)
    } else {
     markLessonFormDirty();
      setLessonForm({ ...lessonForm, type: type as any })
    }
  }
  
  const confirmLessonTypeChangeKeep = () => {
    if (pendingLessonType) {
      markLessonFormDirty();

      setLessonForm({ ...lessonForm, type: pendingLessonType as any })
      setPendingLessonType(null)
      setShowLessonTypeChangeModal(false)
    }
  }
  
  const confirmLessonTypeChangeClear = () => {
    if (pendingLessonType) {
      markLessonFormDirty();

      setLessonForm({ 
        ...lessonForm, 
        type: pendingLessonType as any,
        title: '',
        description: ''
      })
      setPendingLessonType(null)
      setShowLessonTypeChangeModal(false)
    }
  }
  
  const activeModule = modules.find(module => module.id === activeModuleId)
  
  return (
    <div className="space-y-8">
      {toast && (
        <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-white text-sm font-medium transition-all duration-300 ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
          <span>{toast.type === 'success' ? '✓' : '✕'}</span>
          <span>{toast.message}</span>
        </div>
      )}
      <div className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Build your course structure</h2>
        <p className="text-gray-600">Organize content into modules and lessons to create a comprehensive learning path for your students</p>
      </div>
      
      <div className="flex flex-col md:flex-row">
        <div className="w-full md:w-80 border-r border-gray-200 pr-0 md:pr-4 mr-0 md:mr-6 mb-6 md:mb-0">
          <Button 
            onClick={() => {
              setEditingModule(null)
              setModuleForm({ title: '', description: '' })
              setIsModuleModalOpen(true)
            }}
            className="bg-gray-900 hover:bg-gray-800 inline-flex items-center w-full mb-4"
          >
            <Plus className="mr-2" size={18} />
            Create module
          </Button>
          
          {modules.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No modules created yet
            </div>
          ) : (
            <div className="space-y-2">
              {modules.map(module => (
               
                      <div
  key={module.id}
  className="border border-gray-200 rounded-lg overflow-visible"
>
  <div className="flex items-center justify-between p-3 bg-gray-50 min-w-0">
    <div
      className="flex-1 min-w-0 flex items-center cursor-pointer overflow-hidden"
      onClick={() => {
        setActiveModuleId(
          module.id === activeModuleId ? null : module.id
        )
        setShowLessonForm(false)
      }}
    >
                      {module.id === activeModuleId ? (
                          <ChevronDown className="text-gray-500 mr-2 shrink-0" />
                      ) : (
                        <ChevronRight className="text-gray-500 mr-2 shrink-0" />
                      )}
                     <span
  className="font-medium text-gray-900 truncate min-w-0 block"
  title={module.title}
>
  {module.title}
</span>
                
 </div>
<div className="relative shrink-0 ml-2">
<Dropdown
  onOpen={() => {
    setActiveModuleId(module.id)
    setShowLessonForm(false)
  }}
  items={[
    {
      label: 'Edit Module',
      icon: <Edit2 className="mr-2" />,
      onClick: () => handleEditModule(module),
    },
    {
      label: 'Delete Module',
      icon: <Trash2 className="mr-2" />,
      onClick: () => handleDeleteModule(module.id),
      danger: true,
    },
  ]}
>
  <button
    type="button"
    className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
  >
    <MoreVertical size={16} />
  </button>
</Dropdown>
</div>                
                  </div>
                  
                  {module.id === activeModuleId && (
                    <div className="p-2 bg-white">
                      <p className="text-sm text-gray-500 mb-2 line-clamp-2">
                        {module.description || 'No description'}
                      </p>
                      {module.lessons.length === 0 ? (
                        <div className="text-center py-2 text-gray-500 text-sm">
                          No lessons added
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {module.lessons.map(lesson => {
                            const lessonTypeForUI = lesson.type === 'assignment' ? 'document' : lesson.type
                            const lessonType = lessonTypes.find(lt => lt.id === lessonTypeForUI)
                            
                            return (
<div
  key={lesson.id}
  className="px-2 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded flex items-center gap-3 w-full min-w-0"
>
  {/* Icon + Title */}
  <div className="flex items-center gap-2 flex-1 min-w-0">
    <div className="flex items-center justify-center w-6 h-6 shrink-0">
      {lessonType?.icon}
    </div>

    <span
      className="font-medium text-gray-700 truncate min-w-0"
      title={lesson.title}
    >
      {lesson.title}
    </span>

    {lesson.videoStatus === 'processing' && (
      <span className="shrink-0 text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full whitespace-nowrap">
        Processing...
      </span>
    )}

    {lesson.videoStatus === 'failed' && (
      <span className="shrink-0 text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full whitespace-nowrap">
        Processing failed
      </span>
    )}
  </div>

  {/* Duration — reserve space ONLY when duration exists */}
  {lesson.duration ? (
    <span className="text-xs text-gray-500 whitespace-nowrap shrink-0 w-[75px] text-right">
      {formatDuration(lesson.duration)}
    </span>
  ) : null}

  {/* Actions */}
  <div className="flex items-center gap-2 shrink-0">
    <button
      onClick={() => handleEditLesson(lesson)}
      className="text-gray-400 hover:text-blue-500 p-1"
      title="Edit lesson"
    >
      <Edit2 size={16} />
    </button>

    <button
      onClick={() => handleDeleteLesson(lesson.id)}
      className="text-gray-400 hover:text-red-500 p-1"
      title="Delete lesson"
    >
      <Trash2 size={16} />
    </button>
  </div>
</div>                             
                            )
                          })}
                        </div>
                      )}
                      <button 
                        onClick={() => {
                          setEditingLesson(null)
                          resetLessonForm()
                          clearLessonFormDirty()
                          setShowLessonForm(true)
                        }}
                        className="text-blue-600 text-sm mt-2 w-full text-left p-2 hover:bg-blue-50 rounded flex items-center"
                      >
                        <Plus className="mr-2" size={14} />
                        Add lesson
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
        
        <div className="flex-1">
          {modules.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full py-16">
              <div className="max-w-md w-full">
                <h3 className="text-xl font-medium text-gray-900 mb-4 text-center">
                  Create your first module
                </h3>
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <InputField
                    label="Module title"
                    name="title"
                    value={moduleForm.title}
                    onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
                    placeholder="Enter title"
                    required
                    type="text"
                  />
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <div data-color-mode="light">
                      <MDEditor
                        value={moduleForm.description}
                        onChange={(value) => setModuleForm({ ...moduleForm, description: value || "" })}
                        preview="edit"
                        hideToolbar={false}
                        visibleDragbar={false}
                        textareaProps={{
                          placeholder: "Module description...",
                          style: {
                            fontSize: 14,
                            lineHeight: 1.5,
                            fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif'
                          }
                        }}
                        height={200}
                        style={{
                          backgroundColor: 'transparent'
                        }}
                      />
                    </div>
                  </div>
                  <Button
                    onClick={handleCreateModule}
                    disabled={!moduleForm.title.trim() || loading}
                    className="w-full mt-4"
                  >
                    {loading ? 'Creating...' : 'Create Module'}
                  </Button>
                </div>
              </div>
            </div>
          ) : !activeModuleId ? (
            <div className="border border-gray-200 rounded-lg p-6 text-center">
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a module</h3>
              <p className="text-gray-600">Choose a module from the sidebar to view or edit its content</p>
            </div>
          ) : showLessonForm ? (
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingLesson ? 'Edit Lesson' : 'Add New Lesson'}
                </h3>
                <button 
                  onClick={handleCancelLessonForm}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  {lessonTypes.map(type => (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => handleLessonTypeChange(type.id)}
                      className={`flex flex-col items-center justify-center p-3 rounded-lg border ${
                        lessonForm.type === type.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="mb-1">{type.icon}</div>
                      <span className="text-sm">{type.label}</span>
                    </button>
                  ))}
                </div>
                
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    Lesson title <span className="text-red-500">*</span>
  </label>

  <InputField
    type="text"
    label=""
    value={lessonForm.title}
    onChange={(e) => {
      setLessonForm({ ...lessonForm, title: e.target.value });
      markLessonFormDirty();
    }}
    placeholder="Enter lesson title"    
    name="title"
  />
</div>              
                
<div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
  Description <span className="text-red-500">*</span>
</label>               
                  <div data-color-mode="light">
                    <MDEditor
                      value={lessonForm.description}
                      onChange={(value) => {
                        setLessonForm({
                          ...lessonForm,
                          description: value || ""
                        });
                        markLessonFormDirty();
                      }}                      
                      preview="edit"
                      hideToolbar={false}
                      visibleDragbar={false}
                      textareaProps={{
                        placeholder: "Enter lesson description...",
                        style: {
                          fontSize: 14,
                          lineHeight: 1.5,
                          fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif'
                        }
                      }}
                      height={180}
                      style={{
                        backgroundColor: 'transparent'
                      }}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-4">
                  {lessonForm.type !== 'document' && (
                    <div>
<label className="block text-sm font-medium text-gray-700 mb-1 whitespace-nowrap">
  {lessonForm.type === 'video'
    ? 'Duration (auto-calculated)'
    : 'Duration (seconds)'}
</label>                     
                      {lessonForm.type === 'video' ? (
                        <div className="block w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-700 text-sm">
                          {lessonForm.duration ? formatDuration(lessonForm.duration) : <span className="text-gray-400">Duration will be auto-calculated from video</span>}
                        </div>
                      ) : (
                        <input
                          type="number"
                          min="1"
                          value={lessonForm.duration || ''}
                          onChange={(e) => {
                            setLessonForm({
                              ...lessonForm,
                              duration: e.target.value
                                ? parseInt(e.target.value)
                                : null
                            });
                            markLessonFormDirty();
                          }}                          
                          className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Enter duration in seconds"
                        />
                      )}
                      {lessonForm.type === 'video' && (lessonForm.contentFile || videoPreview || editingLesson?.fileUrl) && (
                        <p className="mt-1 text-xs text-gray-500">Duration is automatically calculated for videos</p>
                      )}
                    </div>
                  )}
                </div>
                
                {(lessonForm.type === 'video' || lessonForm.type === 'document') && (
                  <div className="space-y-6">
                    <div>
<label className="block text-sm font-medium text-gray-700 mb-1 whitespace-nowrap">
  {lessonForm.type === 'video' ? (
    <>
      Upload Video (16:9 aspect ratio) <span className="text-red-500">*</span>
    </>
  ) : (
    <>
      Upload Document <span className="text-red-500">*</span>
    </>
  )}
</label>                      

<div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center h-64 flex flex-col items-center justify-center relative">
  {/* Show video/document preview */}
  {videoPreview || (editingLesson?.fileUrl && !lessonForm.contentFile) ? (
    <div className="absolute inset-0 flex flex-col items-center justify-center w-full h-full">

      {lessonForm.type === "video" ? (
        /* =========================
           EXISTING / PREVIEW VIDEO
           ========================= */
        <div className="relative w-full h-full">
<HLSVideoPreview
  src={videoPreview || editingLesson?.fileUrl || ""}
  poster={
    thumbnailPreview ||
    editingLesson?.thumbnailUrl ||
    undefined
  }
/>         
        </div>
      ) : (
        /* =========================
           EXISTING DOCUMENT
           ========================= */
        <div className="text-center">
          <div className="flex items-center justify-center text-green-600 mb-3">
            <FileText className="mr-2" size={24} />

            <span className="font-medium">
              Document already uploaded
            </span>
          </div>

          <p className="text-gray-600 text-sm mb-4">
            Click "Remove" to upload a new document
          </p>
        </div>
      )}

      {/* Remove existing/new uploaded file */}
      {(videoPreview || editingLesson?.fileUrl) && (
        <button
          type="button"
          onClick={handleRemoveVideo}
          className="absolute top-2 right-2 bg-gray-500 text-white rounded-full p-1 hover:bg-gray-600 z-20"
        >
          <X size={16} />
        </button>
      )}
    </div>

  ) : lessonForm.contentFile ? (
    <div className="absolute inset-0 flex flex-col items-center justify-center w-full h-full">

      {lessonForm.type === "video" ? (
        /* NEWLY SELECTED VIDEO */
        <div className="relative w-full h-full">

      <HLSVideoPreview
        src={videoPreview || ""}
        poster={thumbnailPreview || undefined}
      />        

        </div>
      ) : (
        /* NEWLY SELECTED DOCUMENT*/
        <div className="text-center">
          <FileText
            className="text-green-500 mx-auto mb-2"
            size={48}
          />

          <p className="text-gray-700 font-medium">
            {lessonForm.contentFile.name}
          </p>

          <p className="text-sm text-gray-500 mt-1">
            {(lessonForm.contentFile.size / (1024 * 1024)).toFixed(2)}MB
          </p>
        </div>
      )}

      {/* Remove newly selected file */}
      <button
        type="button"
        onClick={handleRemoveVideo}
        className="absolute top-2 right-2 bg-gray-500 text-white rounded-full p-1 hover:bg-gray-600 z-20"
      >
        <X size={16} />
      </button>
    </div>

  ) : (
    /* UPLOAD AREA*/
    <>
      <input
        type="file"
        accept={
          lessonForm.type === "video"
            ? "video/*"
            : ".pdf,.doc,.docx"
        }
        onChange={handleFileUpload}
        className="hidden"
        id="lesson-file-upload"
        required={!editingLesson}
      />

      <label
        htmlFor="lesson-file-upload"
        className="cursor-pointer flex flex-col items-center"
      >
        <Upload
          className="text-gray-400 mb-4"
          size={32}
        />

        <p className="text-gray-600 font-medium mb-2">
          Click to upload{" "}
          {lessonForm.type === "video"
            ? "video"
            : "document"}
        </p>

        <p className="text-sm text-gray-500">
          {lessonForm.type === "video"
            ? "MP4, MOV, AVI (max. 100MB)"
            : "PDF, DOC, DOCX (max. 20MB)"}
        </p>
      </label>
    </>
  )}
</div>                     
                    </div>
                    
                    {lessonForm.type === 'video' && (
                      <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1 whitespace-nowrap">
                      Upload Thumbnail (16:9 aspect ratio) <span className="text-red-500">*</span>
                    </label>                     
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center h-64 flex flex-col items-center justify-center relative">
                          {/* Show thumbnail preview */}
                          {thumbnailPreview || (editingLesson?.thumbnailUrl && !lessonForm.thumbnailFile) ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              <img 
                                src={thumbnailPreview || editingLesson?.thumbnailUrl} 
                                alt="Thumbnail preview" 
                                className="max-h-full max-w-full object-contain"
                              />
                              {/* Show X button for both new files and existing files when editing */}
                              {(thumbnailPreview || editingLesson?.thumbnailUrl) && (
                                <button
                                  type="button"
                                  onClick={handleRemoveThumbnail}
                                  className="absolute top-2 right-2 bg-gray-500 text-white rounded-full p-1 hover:bg-gray-600"
                                >
                                  <X size={16} />
                                </button>
                              )}
                            </div>
                          ) : lessonForm.thumbnailFile ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                              <img 
                                src={URL.createObjectURL(lessonForm.thumbnailFile)} 
                                alt="Thumbnail preview" 
                                className="max-h-full max-w-full object-contain"
                              />
                              <button
                                type="button"
                                onClick={handleRemoveThumbnail}
                                className="absolute top-2 right-2 bg-gray-500 text-white rounded-full p-1 hover:bg-gray-600"
                              >
                                <X size={16} />
                              </button>
                            </div>
                          ) : (
                            <>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleThumbnailUpload}
                                className="hidden"
                                id="thumbnail-upload"
                              />
                              <label htmlFor="thumbnail-upload" className="cursor-pointer flex flex-col items-center">
                                <Upload className="text-gray-400 mb-4" size={32} />
                                <p className="text-gray-600 font-medium mb-2">Click to upload thumbnail</p>
                                <p className="text-sm text-gray-500">JPG, PNG (16:9 aspect ratio recommended)</p>
                              </label>
                            </>
                          )}
                        </div>
                        
                        {thumbnailAspectRatio && (
                          <p className="text-sm text-gray-500 mt-2">
                            Aspect ratio: {thumbnailAspectRatio.toFixed(2)} {Math.abs(thumbnailAspectRatio - 16/9) > 0.05 ? '(Warning: Recommended 16:9 ≈ 1.78)' : '(Good: 16:9 ratio)'}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {lessonForm.type === 'quiz' && (
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quiz Questions
                      </label>
                      <div className="space-y-4">
                        {lessonForm.questions.map((question, qIndex) => (
                          <div key={qIndex} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-3">
                              <h4 className="font-medium text-gray-900">Question {qIndex + 1}</h4>
                              <button
                                type="button"
                                onClick={() => removeQuestion(qIndex)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                            <div className="mb-3">
                              <InputField
                                label="Question"
                                value={question.questionText}
                                onChange={(e) => updateQuestionText(qIndex, e.target.value)}
                                placeholder="Enter your question"
                                required
                                type={""}
                                name={""}
                              />
                            </div>
                            <div className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">Options</span>
                                <button
                                  type="button"
                                  onClick={() => addOption(qIndex)}
                                  className="text-blue-600 hover:text-blue-700 text-sm flex items-center"
                                  disabled={question.options.length >= 6}
                                >
                                  <Plus size={14} className="mr-1" /> Add Option
                                </button>
                              </div>
                              {question.options.map((option, oIndex) => (
                                <div key={oIndex} className="border border-gray-200 rounded-lg p-3">
                                  <div className="flex items-start space-x-2">
                                    <div className="flex items-center h-5 mt-1">
                                      <input
                                        type="radio"
                                        name={`correct-${qIndex}`}
                                        checked={option.isCorrect}
                                        onChange={() => toggleCorrect(qIndex, oIndex)}
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                                        title="Select this as the correct answer"
                                      />
                                    </div>
                                    <div className="flex-1">
                                      <InputField
                                        label={`Option ${oIndex + 1}`}
                                        value={option.answertext}
                                        onChange={(e) => updateOptionText(qIndex, oIndex, e.target.value)}
                                        placeholder="Enter option text"
                                        required
                                        type={""}
                                        name={""}
                                      />
                                      <div className="mt-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                          Explanation (Optional)
                                        </label>
                                        <textarea
                                          value={option.explanation}
                                          onChange={(e) => updateExplanation(qIndex, oIndex, e.target.value)}
                                          placeholder="Explain why this is the correct answer"
                                          rows={2}
                                          className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                        />
                                      </div>
                                    </div>
                                    {question.options.length > 2 && (
                                      <button
                                        type="button"
                                        onClick={() => removeOption(qIndex, oIndex)}
                                        className="text-red-500 hover:text-red-700 mt-2"
                                      >
                                        <X size={16} />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={addQuestion}
                        className="mt-4 flex items-center text-blue-600 hover:text-blue-700"
                      >
                        <Plus size={16} className="mr-1" /> Add Question
                      </button>
                    </div>
                  </div>
                )}
                
                <div className="flex justify-end space-x-3 pt-4">
                  <Button
                    variant="secondary"
                    onClick={handleCancelLessonForm}
                  >
                    Cancel
                  </Button>
                  
                  {/* Upload progress bar — only visible while uploading a video */}
                  {loading && lessonForm.type === 'video' && lessonForm.contentFile && uploadProgress > 0 && uploadProgress < 100 && (
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-xs text-gray-500 mb-1">
                        <span>Uploading video…</span>
                        <span>{uploadProgress}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        />
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={editingLesson ? handleUpdateLesson : handleAddLesson}
                    disabled={!lessonForm.title.trim() ||
                      !lessonForm.description.trim() ||

                      (lessonForm.type === 'quiz' && (
                        lessonForm.questions.length === 0 ||
                        lessonForm.questions.some(q =>
                          !q.questionText.trim() ||
                          q.options.some(o => !o.answertext.trim()) ||
                          !q.options.some(o => o.isCorrect)
                        )
                      )) ||

                      ((lessonForm.type === 'video' || lessonForm.type === 'document') &&
                       !lessonForm.contentFile && !editingLesson?.fileUrl) ||

                       (lessonForm.type === 'video' &&
                        !lessonForm.thumbnailFile &&
                        !thumbnailPreview &&
                        !editingLesson?.thumbnailUrl) ||
                      loading}>

                    {loading ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 10H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {lessonForm.type === 'video' && lessonForm.contentFile && uploadProgress > 0 && uploadProgress < 100
                          ? `Uploading ${uploadProgress}%…`
                          : editingLesson ? 'Updating...' : 'Creating...'}
                      </>
                    ) : editingLesson ? 'Update Lesson' : 'Add Lesson'}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="border-b pb-4 mb-2">
                <h3 className="text-md font-medium text-gray-900">{activeModule?.title}</h3>
              </div>

            <div className="flex items-center justify-between gap-3 mb-4">
              <h3 className="text-lg font-semibold">
                Lessons
              </h3>

  {/* <button
    type="button"
    onClick={handleSaveLessonOrder}
    disabled={!hasUnsavedLessonOrder || savingLessonOrder}
    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
      hasUnsavedLessonOrder && !savingLessonOrder
        ? 'bg-green-600 text-white hover:bg-green-700'
        : 'bg-gray-200 text-gray-500 cursor-not-allowed'
    }`}
  >
    {savingLessonOrder ? 'Saving...' : 'Save Order'}
  </button> */}
</div>

<DragDropContext onDragEnd={onDragEnd}>
  <Droppable droppableId="lessons">
    {(provided) => (
      <div
        {...provided.droppableProps}
        ref={provided.innerRef}
        className="space-y-4"
      >
        {activeModule?.lessons.length === 0 ? (
          // Empty state
          <div className="text-center py-8">
            <p className="text-gray-500 mb-4">
              This module doesn't have any lessons yet
            </p>

            <Button
              onClick={() => {
                setEditingLesson(null)
                resetLessonForm()
                clearLessonFormDirty()
                setShowLessonForm(true)
              }}
              className="bg-gray-900 hover:bg-gray-800 inline-flex items-center"
            >
              <Plus className="mr-2" size={18} />
              Add your first lesson
            </Button>
          </div>
        ) : (
          // Existing lessons
          activeModule?.lessons.map((lesson, index) => {
            const lessonTypeForUI =
              lesson.type === 'assignment'
                ? 'document'
                : lesson.type

            const lessonType = lessonTypes.find(
              lt => lt.id === lessonTypeForUI
            )

            return (
              <Draggable
                key={lesson.id}
                draggableId={lesson.id}
                index={index}
              >
                {(provided) => (
<div
  ref={provided.innerRef}
  {...provided.draggableProps}
  className="lesson-card border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow bg-white"
>
 {/* TOP ROW */}
<div className="lesson-card-top">

  {/* Lesson type icon */}
  <div
    className={`lesson-type-icon p-2 rounded-lg ${lessonType?.color}`}
  >
    {lessonType?.icon}
  </div>

  {/* Edit + Delete + Move */}
  <div className="lesson-card-actions">

    {/* Edit */}
    <button
      onClick={() => handleEditLesson(lesson)}
      className="text-gray-400 hover:text-blue-500"
      title="Edit lesson"
    >
      <Edit2 size={18} />
    </button>

    {/* Delete */}
    <button
      onClick={() => handleDeleteLesson(lesson.id)}
      className="text-gray-400 hover:text-red-500"
      title="Delete lesson"
    >
      <Trash2 size={18} />
    </button>

    {/* Move - LAST */}
    <div
      {...provided.dragHandleProps}
      className="lesson-drag-handle"
      title="Move lesson"
    >
      <Move size={16} />
    </div>

  </div>

</div>

  {/* CONTENT BELOW */}
  <div className="lesson-card-content">

    <div className="flex items-start gap-2 min-w-0">
      <span className="text-sm font-medium text-gray-500 shrink-0">
        {index + 1}.
      </span>

      <h4 className="font-medium text-gray-900 break-words min-w-0">
        {lesson.title}
      </h4>
    </div>

    <p className="text-sm text-gray-500 mt-2">
      {lessonType?.label} •{' '}
      {lesson.duration
        ? formatDuration(lesson.duration)
        : ''}
    </p>

    {lesson.description && (
      <p className="text-sm text-gray-600 mt-4 line-clamp-2">
        {lesson.description}
      </p>
    )}

    {/* Document preview */}
    {lesson.fileUrl &&
      lessonTypeForUI === 'document' && (
        // keep your existing document preview here
        <div className="mt-2">
          ...
        </div>
      )}

    {/* Quiz question count */}
    {lessonTypeForUI === 'quiz' &&
      lesson.questions &&
      lesson.questions.length > 0 && (
        <div className="mt-4 text-sm text-gray-600">
          {lesson.questions.length} question
          {lesson.questions.length !== 1 ? 's' : ''}
        </div>
      )}

  </div>
</div>                  
                )}
              </Draggable>
            )
          })
        )}

        {provided.placeholder}
      </div>
    )}
  </Droppable>

  {/* Add new lesson button */}
  <button
    onClick={() => {
      setEditingLesson(null)
      resetLessonForm()
      clearLessonFormDirty()
      setShowLessonForm(true)
    }}
    className="w-full mt-4 border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors flex flex-col items-center"
  >
    <Plus className="text-gray-400 mb-2" size={20} />

    <span className="text-gray-600 font-medium">
      Add new lesson
    </span>
  </button>
</DragDropContext>
            </div>
          )}
        </div>
      </div>
    
      <Modal isOpen={isModuleModalOpen} onClose={() => {
        setIsModuleModalOpen(false)
        setEditingModule(null)
        setModuleForm({ title: '', description: '' })
      }}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {editingModule ? 'Edit Module' : 'Create New Module'}
            </h3>
            <button 
              onClick={() => {
                setIsModuleModalOpen(false)
                setEditingModule(null)
                setModuleForm({ title: '', description: '' })
              }}
              className="text-gray-400 hover:text-gray-500"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="space-y-4">
            <InputField
              label="Module title"
              name="title"
              value={moduleForm.title}
              onChange={(e) => setModuleForm({ ...moduleForm, title: e.target.value })}
              placeholder="Enter title"
              required
              type="text"
            />
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <div data-color-mode="light">
                <MDEditor
                  value={moduleForm.description}
                  onChange={(value) => setModuleForm({ ...moduleForm, description: value || "" })}
                  preview="edit"
                  hideToolbar={false}
                  visibleDragbar={false}
                  textareaProps={{
                    placeholder: "Please write your description here...",
                    style: {
                      fontSize: 14,
                      lineHeight: 1.5,
                      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif'
                    }
                  }}
                  height={150}
                  style={{
                    backgroundColor: 'transparent'
                  }}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setIsModuleModalOpen(false)
                  setEditingModule(null)
                  setModuleForm({ title: '', description: '' })
                }}
              >
                Cancel
              </Button>
              
              <Button
                onClick={editingModule ? handleUpdateModule : handleCreateModule}
                disabled={!moduleForm.title.trim() || loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 10H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {editingModule ? 'Updating...' : 'Creating...'}
                  </>
                ) : editingModule ? 'Update Module' : 'Create Module'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      
      <Modal 
        isOpen={showLessonTypeChangeModal} 
        onClose={() => {
          setShowLessonTypeChangeModal(false)
          setPendingLessonType(null)
        }}
      >
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Confirm Lesson Type Change
            </h3>
            <button 
              onClick={() => {
                setShowLessonTypeChangeModal(false)
                setPendingLessonType(null)
              }}
              className="text-gray-400 hover:text-gray-500"
            >
              <X size={20} />
            </button>
          </div>
          
          <div className="space-y-4">
            <p className="text-gray-600">
              You have entered content in the title and description fields. Do you want to keep this content or clear it when changing the lesson type?
            </p>
            
            <div className="flex justify-end space-x-3 pt-4">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowLessonTypeChangeModal(false)
                  setPendingLessonType(null)
                }}
              >
                Cancel
              </Button>
              
              <Button
                onClick={confirmLessonTypeChangeClear}
                className="bg-red-600 hover:bg-red-700"
              >
                Clear Content
              </Button>
              
              <Button
                onClick={confirmLessonTypeChangeKeep}
              >
                Keep Content
              </Button>
            </div>
          </div>
        </div>
      </Modal>
      
    
    </div>
  )
}

export default CurriculumStep